import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Checkout = () => {
  const { user } = useAuth();
  const { cartItems, getCartTotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [shippingInfo, setShippingInfo] = useState({
    full_name: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    phone: ''
  });

  if (!user) {
    navigate('/login');
    return null;
  }

  if (cartItems.length === 0) {
    navigate('/cart');
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const orderData = {
        items: cartItems.map(item => ({
          product_id: item.product_id,
          product_name: item.product?.name,
          quantity: item.quantity,
          price: item.product?.price
        })),
        total_amount: getCartTotal(),
        shipping_address: shippingInfo
      };

      await axios.post(`${API}/orders`, orderData);
      toast.success('Order placed successfully! Check your email for confirmation.');
      navigate('/orders');
    } catch (error) {
      console.error('Failed to create order:', error);
      toast.error('Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAFAFA' }}>
      <div className="pt-32 pb-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-6xl mx-auto">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl mb-12"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="checkout-title"
          >
            Checkout
          </motion.h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Shipping Form */}
            <div className="lg:col-span-2">
              <motion.form
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                onSubmit={handleSubmit}
                className="bg-white p-8 rounded-lg border border-black/5"
                data-testid="checkout-form"
              >
                <h2 className="text-2xl mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Shipping Information
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm uppercase tracking-widest mb-2" htmlFor="full_name">
                      Full Name *
                    </label>
                    <input
                      id="full_name"
                      type="text"
                      value={shippingInfo.full_name}
                      onChange={(e) => setShippingInfo({ ...shippingInfo, full_name: e.target.value })}
                      data-testid="checkout-fullname"
                      required
                      className="w-full px-4 py-3 border border-[#E5E5E5] rounded-lg focus:outline-none focus:border-[#1A1A1A] transition-colors"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm uppercase tracking-widest mb-2" htmlFor="address">
                      Address *
                    </label>
                    <input
                      id="address"
                      type="text"
                      value={shippingInfo.address}
                      onChange={(e) => setShippingInfo({ ...shippingInfo, address: e.target.value })}
                      data-testid="checkout-address"
                      required
                      className="w-full px-4 py-3 border border-[#E5E5E5] rounded-lg focus:outline-none focus:border-[#1A1A1A] transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm uppercase tracking-widest mb-2" htmlFor="city">
                      City *
                    </label>
                    <input
                      id="city"
                      type="text"
                      value={shippingInfo.city}
                      onChange={(e) => setShippingInfo({ ...shippingInfo, city: e.target.value })}
                      data-testid="checkout-city"
                      required
                      className="w-full px-4 py-3 border border-[#E5E5E5] rounded-lg focus:outline-none focus:border-[#1A1A1A] transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm uppercase tracking-widest mb-2" htmlFor="state">
                      State *
                    </label>
                    <input
                      id="state"
                      type="text"
                      value={shippingInfo.state}
                      onChange={(e) => setShippingInfo({ ...shippingInfo, state: e.target.value })}
                      data-testid="checkout-state"
                      required
                      className="w-full px-4 py-3 border border-[#E5E5E5] rounded-lg focus:outline-none focus:border-[#1A1A1A] transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm uppercase tracking-widest mb-2" htmlFor="zip_code">
                      ZIP Code *
                    </label>
                    <input
                      id="zip_code"
                      type="text"
                      value={shippingInfo.zip_code}
                      onChange={(e) => setShippingInfo({ ...shippingInfo, zip_code: e.target.value })}
                      data-testid="checkout-zip"
                      required
                      className="w-full px-4 py-3 border border-[#E5E5E5] rounded-lg focus:outline-none focus:border-[#1A1A1A] transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm uppercase tracking-widest mb-2" htmlFor="phone">
                      Phone *
                    </label>
                    <input
                      id="phone"
                      type="tel"
                      value={shippingInfo.phone}
                      onChange={(e) => setShippingInfo({ ...shippingInfo, phone: e.target.value })}
                      data-testid="checkout-phone"
                      required
                      className="w-full px-4 py-3 border border-[#E5E5E5] rounded-lg focus:outline-none focus:border-[#1A1A1A] transition-colors"
                    />
                  </div>
                </div>

                <div className="pt-6 border-t border-black/5">
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="checkout-submit-btn"
                    className="w-full bg-[#1A1A1A] text-white hover:bg-[#333333] rounded-full px-8 py-6 text-sm uppercase tracking-widest transition-all duration-300 disabled:opacity-50"
                  >
                    {loading ? 'Processing...' : 'Place Order'}
                  </button>
                  <p className="text-center text-sm text-[#888888] mt-4">
                    Note: Payment integration will be added. This is a demo order.
                  </p>
                </div>
              </motion.form>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white p-8 rounded-lg border border-black/5 sticky top-32"
              >
                <h3 className="text-2xl mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Order Summary
                </h3>
                <div className="space-y-4 mb-6">
                  {cartItems.map((item) => (
                    <div key={item.product_id} className="flex justify-between text-sm">
                      <span className="text-[#888888]">{item.product?.name} x{item.quantity}</span>
                      <span>${(item.product?.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div className="pt-6 border-t border-black/5">
                  <div className="flex justify-between text-xl font-bold">
                    <span>Total</span>
                    <span data-testid="checkout-total">${getCartTotal().toFixed(2)}</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
